<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'dark' | 'quiet' | 'destructive' | 'destructive-solid'
    disabled?: boolean
    loading?: boolean
    block?: boolean
    /**
     * When set, the button collapses to this label on narrow viewports
     * (<= 540px) and the square form factor matching its height. Used
     * by dashboard headers so a long verb like "+ Legg til manuelt"
     * compresses to just "+" rather than wrapping below the page title
     * on phones. The default slot is the full label; this prop is the
     * compact replacement (typically a "+" glyph). The full label is
     * still exposed to screen readers via aria-label-equivalence — we
     * leave the slot content rendered (hidden visually) so SR users
     * hear the intent rather than just "plus".
     */
    compactLabel?: string
    /**
     * Native button type. Defaults to "button" so a stray StemneButton
     * inside a form doesn't accidentally submit on click (#1359 root
     * cause). Form submit handlers must pass type="submit" explicitly.
     */
    type?: 'button' | 'submit' | 'reset'
    /**
     * When set, renders an <a> with this href instead of a <button> —
     * for CTAs that are semantically navigation (mailto:, external
     * URLs). A real href works without JS, is announced as a link by
     * AT, and supports middle-click/copy-link (#1598 root cause: a
     * button whose click handler set window.location read as inert).
     * disabled/loading don't apply to the anchor form.
     */
    href?: string
  }>(),
  {
    variant: 'primary',
    disabled: false,
    loading: false,
    block: false,
    compactLabel: undefined,
    type: 'button',
    href: undefined,
  },
)

const classes = computed(() => [
  'btn',
  `btn--${props.variant}`,
  {
    'btn--block': props.block,
    'btn--loading': props.loading,
    'btn--compactable': props.compactLabel != null,
  },
])
</script>

<template>
  <a v-if="href" :href="href" :class="classes" data-vaul-no-drag>
    <span class="btn__label btn__label--full">
      <slot />
    </span>
    <span v-if="compactLabel" class="btn__label btn__label--compact" aria-hidden="true">
      {{ compactLabel }}
    </span>
  </a>
  <button
    v-else
    :type="type"
    :class="classes"
    :disabled="disabled || loading"
    :aria-busy="loading || undefined"
    data-vaul-no-drag
  >
    <span v-if="loading" class="btn__spinner" aria-hidden="true" />
    <span class="btn__label btn__label--full" :class="{ 'btn__label--hidden': loading }">
      <slot />
    </span>
    <span
      v-if="compactLabel"
      class="btn__label btn__label--compact"
      :class="{ 'btn__label--hidden': loading }"
      aria-hidden="true"
    >
      {{ compactLabel }}
    </span>
  </button>
</template>

<style lang="scss" scoped>
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 var(--space-6);
  border-radius: 10px;
  border: 1px solid transparent;
  font-family: var(--font-sans);
  font-size: var(--fs-body-sm);
  font-weight: var(--fw-medium);
  line-height: 1;
  cursor: pointer;
  // Anchor form (href prop) — keep the button look on <a>.
  text-decoration: none;
  transition:
    background var(--duration-fade) var(--ease-default),
    color var(--duration-fade) var(--ease-default),
    border-color var(--duration-fade) var(--ease-default);
  position: relative;

  &:disabled {
    // #1236 — pre-fix, this rule was `opacity: 0.5`, which on the
    // moss-on-cream primary variant produced a near-cream pill with
    // washed-out cream text. Operators read it as a button that
    // disappeared rather than a disabled CTA. Per-variant disabled
    // styles below replace the blanket opacity; this rule keeps only
    // the cursor cue, which works for every variant.
    cursor: not-allowed;
  }
}

.btn--block {
  display: flex;
  width: 100%;
}

/* -- Variants -- */

.btn--primary {
  background: var(--button-primary-bg, var(--moss));
  border-color: var(--button-primary-border, transparent);
  color: var(--surface-cream);

  &:hover:not(:disabled) {
    background: var(--button-primary-hover, var(--moss-hover));
  }

  // #1236 — filled variants used to fall under the global opacity: 0.5,
  // which on the moss-on-cream pill produced near-cream washed text on
  // a near-cream background — the button read as "missing" rather than
  // "disabled". Per-variant disabled colors give an explicit inactive
  // tone that meets WCAG AA 3:1 non-text contrast against the cream
  // surface AND clearly distinguishes from the active state.
  &:disabled {
    background: var(--border-stronger);
    color: var(--graphite);
    border-color: var(--border-stronger);
  }
}

.btn--secondary {
  background: transparent;
  color: var(--fg-1);
  border-color: var(--border-stronger);

  &:hover:not(:disabled) {
    background: var(--surface-warm-beige);
  }

  // #1236 — text-only variants: muted text + softened border. The fg-1
  // (ink) at full opacity wouldn't read as inactive against cream; the
  // muted token + reduced border contrast is the clear "off" cue
  // without the missing-button class of bug from the global opacity.
  &:disabled {
    color: var(--fg-muted);
    border-color: var(--border-subtle);
  }
}

.btn--dark {
  background: var(--ink);
  color: var(--surface-cream);

  &:hover:not(:disabled) {
    background: #2e2a24;
  }

  // #1236 — same filled-button pattern as primary, same rationale.
  &:disabled {
    background: var(--border-stronger);
    color: var(--graphite);
    border-color: var(--border-stronger);
  }
}

.btn--quiet {
  background: transparent;
  color: var(--fg-2);
  border-color: transparent;

  &:hover:not(:disabled) {
    color: var(--fg-1);
  }

  &:disabled {
    color: var(--fg-muted);
  }
}

.btn--destructive {
  background: transparent;
  color: var(--fg-destructive);
  border-color: transparent;

  &:hover:not(:disabled) {
    background: var(--rust-low);
  }

  // #1236 — drop the rust color entirely when disabled. A faded rust
  // still reads as "I am about to do something destructive"; the muted
  // grey makes the inactive state unambiguous.
  &:disabled {
    color: var(--fg-muted);
  }
}

// Filled rust — used for irreversible operator actions where the
// destructive verb is the primary CTA on the surface (e.g. "Avlys og
// varsle"). The text-only `destructive` variant is for customer-facing
// or quiet-secondary contexts where the action shouldn't dominate.
.btn--destructive-solid {
  background: var(--rust);
  color: var(--surface-cream);
  border-color: var(--rust);

  &:hover:not(:disabled) {
    background: #732f1d;
    border-color: #732f1d;
  }

  // #1236 — same filled-button pattern as primary + dark.
  &:disabled {
    background: var(--border-stronger);
    color: var(--graphite);
    border-color: var(--border-stronger);
  }
}

/* -- Spinner -- */

.btn__spinner {
  position: absolute;
  width: 18px;
  height: 18px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: btn-spin 600ms linear infinite;
}

.btn__label--hidden {
  visibility: hidden;
}

// Compact label only renders above the phone breakpoint as a no-op —
// the full slot wins. The compact span swaps in at the breakpoint
// below; this keeps the desktop output unchanged.
.btn__label--compact {
  display: none;
  font-size: var(--fs-h3);
  line-height: 1;
}

@media (max-width: 540px) {
  // Standard mobile compaction for "+ Verb" headline actions: square
  // up the button to a 40pt touch target with just the compact glyph.
  // The full slot stays in the DOM (for AT) but is visually hidden.
  .btn--compactable {
    width: 40px;
    min-width: 40px;
    min-height: 40px;
    padding: 0;
    border-radius: 999px;
    flex-shrink: 0;

    .btn__label--full {
      // Visually hide the full label without removing it from the
      // accessibility tree — screen readers still announce the long
      // form so the button keeps its intent.
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .btn__label--compact {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  }
}

@keyframes btn-spin {
  to { transform: rotate(360deg); }
}
</style>
